import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Origin } from '@1po/1po-bff-fe-spec/generated/basket/response/BasketResponse';

import { ReferenceKey } from '@1po/1po-bff-fe-spec/generated/catalog/references/common/model/ReferenceKey';
import { OEMCompatibility } from '@1po/1po-bff-fe-spec/generated/catalog/references/iam/model/OEMCompatibility';
import { ReferenceStock } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/model/ReferenceStock';
import { CatalogSource } from '@1po/1po-bff-fe-spec/generated/common/vehicle/CatalogSource';
import { ReferenceLocal as BasketReference } from 'domains/basket/Basket.types';
import { IAM, MOTRIO } from 'domains/catalog/Catalog.types';
import { DHReferenceLocal, IAMReferenceLocal, NON_MKTP, ReferenceLocal } from 'domains/references/References.types';
import { getSearchData, NO_DATA, SearchData } from 'utils';
import { getStocksMapV2 } from './References.store';
import { RootState } from '../../app/AppStore';
import { getBasketIAMWithoutMotrioReferences } from '../basket/Basket.store';

export function isRefMKTP(reference?: ReferenceLocal): boolean {
  if (reference !== undefined && 'referenceSource' in reference) return reference.referenceSource === 'MARKETPLACE';
  return false;
}

export function isBasketRefExadis(reference: BasketReference): boolean {
  return isReferenceExadis(reference.catalogSource, reference.brand, reference.origin);
}

export function isReferenceExadis(
  catalogSource: CatalogSource,
  brand: string | undefined,
  origin: Origin | undefined,
): boolean {
  return catalogSource === IAM && brand !== MOTRIO && origin === IAM;
}

export const getExadisStockInfoWithDeliveryToday = (
  stockInfo: SearchData<ReferenceStock> | undefined,
): SearchData<ReferenceStock> | undefined => {
  const refStockData = getSearchData(stockInfo);

  if (refStockData) {
    const formattedToday = new Date().toISOString().split('T')[0];
    const exadisStocks = (stockInfo?.data?.warehouses ?? []).filter((warehouse) => warehouse.type === 'EXADIS');
    const exadisStocksWithDeliveryToday = exadisStocks.filter((warehouse) =>
      warehouse.expectedDeliveryDate?.startsWith(formattedToday),
    );
    const exadisConfirmedQuantity: number = exadisStocksWithDeliveryToday.reduce((sum, obj) => {
      return sum + (obj && 'confirmedQuantity' in obj ? Number(obj.confirmedQuantity) || 0 : 0);
    }, 0);

    return {
      ...stockInfo,
      data: { ...refStockData, confirmedQuantity: exadisConfirmedQuantity, warehouses: exadisStocksWithDeliveryToday },
    } as SearchData<ReferenceStock>;
  } else {
    return stockInfo;
  }
};

export function isReferenceEqual(ref1: ReferenceKey, ref2: ReferenceKey): boolean {
  return ref1.referenceNumber === ref2.referenceNumber && ref1.sellerNumber === ref2.sellerNumber;
}

export function matchesReferenceKey(referenceLocal: BasketReference, referenceKey: ReferenceKey): boolean {
  return (
    referenceLocal.referenceNumber === referenceKey.referenceNumber &&
    referenceLocal.sellerNumber === referenceKey.sellerNumber
  );
}

export function matchesReferenceByKey(
  referenceLocal: BasketReference,
  referenceNumber: string,
  sellerNumber?: string,
): boolean {
  return referenceLocal.referenceNumber === referenceNumber && referenceLocal.sellerNumber === sellerNumber;
}

export function formatReferenceLabel(referenceLabel: string, referenceNumber: string): string {
  return `${referenceLabel.trim()} ${referenceNumber}`;
}

export const useAllStocksBasketIAMWithoutMotrioReferences = (): Map<
  string,
  SearchData<Map<string, ReferenceStock>> | undefined
> => {
  const basketIAMWithoutMotrioReferences = useSelector(getBasketIAMWithoutMotrioReferences);

  return useSelector((state: RootState) =>
    getStocksMapV2(
      state,
      basketIAMWithoutMotrioReferences.map((l) => l.referenceNumber),
    ),
  );
};

export const useBasketIAMWithoutMotrioReferencesCount = (): number => {
  const basketIAMWithoutMotrioReferences = useSelector(getBasketIAMWithoutMotrioReferences);
  const allStocksBasketIAMWithoutMotrioReferences = useAllStocksBasketIAMWithoutMotrioReferences();

  return useMemo(() => {
    return basketIAMWithoutMotrioReferences.filter((r) => {
      const stockData = allStocksBasketIAMWithoutMotrioReferences
        ?.get(r.referenceNumber)
        ?.data?.get(r.sellerNumber ?? NON_MKTP);
      return !stockData?.warehouses.some((w) => w.type === 'LOCAL' && w.confirmedQuantity > 0);
    }).length;
  }, [basketIAMWithoutMotrioReferences, allStocksBasketIAMWithoutMotrioReferences]);
};

function isReferenceObject(ref: NO_DATA | DHReferenceLocal | IAMReferenceLocal): ref is DHReferenceLocal {
  return ref != null && typeof ref === 'object' && 'supersessionChainReferences' in ref;
}

// Builds the supersession chain for a reference, latest to oldest.
function buildSupersessionChain(
  referenceNumber: string,
  dhRefs: Map<string, NO_DATA | DHReferenceLocal>,
  iamRefs: Map<string, NO_DATA | IAMReferenceLocal>,
): string[] {
  const visited = new Set<string>();

  const collect = (currentNum: string | undefined, chain: string[]): string[] => {
    if (!currentNum || visited.has(currentNum)) {
      return chain;
    }

    visited.add(currentNum);
    const nextChain = [...chain, currentNum];
    const ref = dhRefs.get(currentNum) ?? iamRefs.get(currentNum);
    const nextRef =
      isReferenceObject(ref) && ref.supersessionChainReferences && ref.supersessionChainReferences.length > 0
        ? ref.supersessionChainReferences[0]
        : undefined;

    return collect(nextRef, nextChain);
  };

  return collect(referenceNumber, []);
}

export function getLatestInStockSupersessionReference(referenceNumber: string, state: RootState): string {
  const dhRefs = state.references.references.dhReferences.references;
  const iamRefs = state.references.references.iamReferences.references;

  // Build chain (latest to oldest)
  const chain = buildSupersessionChain(referenceNumber, dhRefs, iamRefs);

  // Helper to check stock
  const hasStock = (refNum: string): boolean => {
    const stockData = state.references.stockInfoV2.get(refNum);
    const stockMap = stockData?.data;
    return (
      stockMap instanceof Map &&
      Array.from(stockMap.values()).some(
        (stock) => stock && Array.isArray(stock.warehouses) && stock.warehouses.some((w) => w.confirmedQuantity > 0),
      )
    );
  };

  // Return first in-stock reference
  const found = chain.find(hasStock);
  return found || referenceNumber;
}

export const getMatchingOEMCompatibility = (
  oemCompatibilities: OEMCompatibility[] | undefined,
  vehicleBrandCode: string | undefined,
  vehicleBrand?: string | undefined,
): OEMCompatibility | undefined => {
  if (!oemCompatibilities?.length) {
    return undefined;
  }

  // First try to find exact match by supplierCode
  if (vehicleBrandCode) {
    const exactMatch = oemCompatibilities.find((oem) => oem.supplierCode === vehicleBrandCode);
    if (exactMatch) {
      return exactMatch;
    }
  }

  // Fallback: try to find exact match by supplierLabel
  if (vehicleBrand) {
    const normalizedBrand = vehicleBrand.toUpperCase();
    const labelMatch = oemCompatibilities.find((oem) => oem.supplierLabel?.toUpperCase() === normalizedBrand);
    if (labelMatch) {
      return labelMatch;
    }
  }

  return oemCompatibilities[0];
};

export const isOtherBrandVehicle = (vehicleBrand: string | undefined): boolean => {
  if (!vehicleBrand) {
    return false;
  }
  const renaultGroupBrands = ['RENAULT', 'DACIA', 'ALPINE'];
  return !renaultGroupBrands.includes(vehicleBrand.toUpperCase());
};
